import type { WindowGridLayout } from "building-types/settings/windowGridLayout";
import type { WindowGridPosition } from "building-types/settings/windowGridPosition";
import { degreeToRadian } from "common/utils/math";
import { aabb2CenterWidthHeight } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { arc2ByCenterStartAngle } from "geom/arc";
import { TWO_PI } from "geom/core";
import { polyLine2NonUniformScaleAndKeepCenter } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { rangeNonInclusiveByCount } from "geom/range";

export function regularOrthogonalWindowGrid(
  paneAABB: AABB2,
  divs: number,
  thickness: number,
  vertical: boolean,
): PolyLine2[] {
  if (divs <= 1) {
    return [];
  }

  const { minX, maxX, minY, maxY } = paneAABB;
  const start = vertical ? minX : minY;
  const end = vertical ? maxX : maxY;

  const starts = rangeNonInclusiveByCount(start - thickness / 2, end + thickness / 2, divs - 1);

  const edges: PolyLine2[] = [];

  if (vertical) {
    for (const start of starts) {
      edges.push(polyLine2New([start, maxY, start, minY]));
    }
  } else {
    for (const start of starts) {
      edges.push(polyLine2New([minX, start, maxX, start]));
    }
  }

  return edges;
}

export function customOrthogonalWindowGrid(
  paneAABB: AABB2,
  layout: WindowGridLayout | undefined,
  vertical: boolean,
): PolyLine2[] {
  if (!layout) {
    return [];
  }
  const { minX, maxX, minY, maxY } = paneAABB;
  const [start, alignedStart] = vertical ? [minX, minY] : [minY, minX];
  const [end, alignedEnd] = vertical ? [maxX, maxY] : [maxY, maxX];

  const edgeLength = end - start;
  const alignedEdgeLength = alignedEnd - alignedStart; // used for trimming grid lines

  const { positions, start: trimStart, startNormalized, end: trimEnd, endNormalized } = layout;

  const normalizedTrimStart = startNormalized ? trimStart : trimStart / alignedEdgeLength;
  const normalizedTrimEnd = endNormalized ? trimEnd : trimEnd / alignedEdgeLength;

  const lengthTrimStart = vertical ? (maxY - minY) * normalizedTrimStart : (maxX - minX) * normalizedTrimStart;
  const lengthTrimEnd = vertical ? (maxY - minY) * (1 - normalizedTrimEnd) : (maxX - minX) * (1 - normalizedTrimEnd);

  const lengthPosition = (p: WindowGridPosition) => {
    const pos = p.valueNormalized ? edgeLength * p.value : p.value;
    return pos < 0 ? edgeLength + pos : pos;
  };

  const edges: PolyLine2[] = [];

  if (vertical) {
    for (const p of positions) {
      const pos = lengthPosition(p);
      edges.push(polyLine2New([pos + start, maxY - lengthTrimStart, pos + start, minY + lengthTrimEnd]));
    }
  } else {
    for (const p of positions) {
      const pos = lengthPosition(p);
      edges.push(polyLine2New([minX + lengthTrimStart, end - pos, maxX - lengthTrimEnd, end - pos]));
    }
  }

  return edges;
}

export function customRadialWindowGrid(paneAABB: AABB2, layout: WindowGridLayout | undefined): PolyLine2[] {
  if (!layout) {
    return [];
  }

  const [centerX, centerY, width, height] = aabb2CenterWidthHeight(paneAABB);
  const scaleX = height > width;
  const radius = scaleX ? height / 2 : width / 2;
  const ellipseScaleXFactor = scaleX ? width / height : 1;
  const ellipseScaleYFactor = scaleX ? 1 : height / width;

  const { positions, start, startNormalized, end, endNormalized } = layout;
  const normalizedTrimStart = startNormalized ? start : start / radius;
  const normalizedTrimEnd = endNormalized ? end : end / radius;
  const edges: PolyLine2[] = [];

  for (const p of positions) {
    const { value, valueNormalized } = p;
    const rads = valueNormalized ? value * TWO_PI : degreeToRadian(value);
    const xLength = Math.cos(rads) * radius * ellipseScaleXFactor;
    const yLength = Math.sin(rads) * radius * ellipseScaleYFactor;
    edges.push(
      polyLine2New([
        centerX + xLength * normalizedTrimStart,
        centerY + yLength * normalizedTrimStart,
        centerX + xLength * normalizedTrimEnd,
        centerY + yLength * normalizedTrimEnd,
      ]),
    );
  }

  return edges;
}

export function customCircularWindowGrid(paneAABB: AABB2, layout: WindowGridLayout | undefined): PolyLine2[] {
  if (!layout) {
    return [];
  }

  const [centerX, centerY, width, height] = aabb2CenterWidthHeight(paneAABB);
  const scaleX = height > width;
  const radius = scaleX ? height / 2 : width / 2;
  const ellipseScaleXFactor = scaleX ? width / height : 1;
  const ellipseScaleYFactor = scaleX ? 1 : height / width;

  const { positions, start, startNormalized, end, endNormalized } = layout;
  const radsTrimStart = startNormalized ? start * TWO_PI : degreeToRadian(start);
  const radsTrimEnd = endNormalized ? end * TWO_PI : degreeToRadian(end);
  const edges: PolyLine2[] = [];

  for (const p of positions) {
    const { value, valueNormalized } = p;
    const lengthPosition = valueNormalized ? value * radius : value;
    const arcStartX = centerX + Math.cos(radsTrimStart) * lengthPosition;
    const arcStartY = centerY + Math.sin(radsTrimStart) * lengthPosition;
    const arc = arc2ByCenterStartAngle(
      centerX,
      centerY,
      arcStartX,
      arcStartY,
      radsTrimEnd - radsTrimStart,
      0.01,
      10,
      30,
    );

    edges.push(polyLine2NonUniformScaleAndKeepCenter(arc, ellipseScaleXFactor, ellipseScaleYFactor));
  }

  return edges;
}
